import {
  ChangeDetectionStrategy,
  Component,
  effect,
  input,
  model,
  output,
  viewChild,
  inject,
} from '@angular/core';
import { MatFormField, MatFormFieldModule } from '@angular/material/form-field';
import {
  MatDatepicker,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import { MatInputModule } from '@angular/material/input';
import {
  DateAdapter,
  MatNativeDateModule,
  provideNativeDateAdapter,
} from '@angular/material/core';
import { NgClass } from '@angular/common';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { DATEPICKER_FORMAT_M, MAX_DATE_M, MIN_DATE_M } from '@core-utils';
import { SwitchLanguageService } from '../../services';
import {
  AbstractControl,
  FormControl,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import { ValidationFormComponent } from '../validation-form/validation-form.component';
import { RangeDatepickerMInterface } from '../../types';
import { TranslocoDirective } from '@jsverse/transloco';
import { uiKitScope } from '../../constants/ui-constant';
import { FormRangeDatepickerHeader } from './form-range-datepicker-header';
import { provideUiKitScope } from '../../providers';

@Component({
  selector: 'ui-form-range-datepicker',
  imports: [
    MatFormField,
    MatFormFieldModule,
    NgClass,
    MatDatepickerModule,
    MatInputModule,
    FormsModule,
    ReactiveFormsModule,
    ValidationFormComponent,
    TranslocoDirective,
    MatNativeDateModule,
  ],
  templateUrl: './form-range-datepicker.component.html',
  styleUrl: './form-range-datepicker.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    { provide: MAT_DATE_FORMATS, useValue: DATEPICKER_FORMAT_M },
    provideNativeDateAdapter(),
    provideUiKitScope(),
  ],
})
export class FormRangeDatepickerComponent {
  private dateAdapter = inject<DateAdapter<Date>>(DateAdapter);
  private switchLanguageService = inject(SwitchLanguageService);

  protected readonly formRangeDatepickerHeader = FormRangeDatepickerHeader;
  protected readonly uiKitScope = uiKitScope;
  pickerMat = viewChild.required<MatDatepicker<Date>>('pickerMat');
  control = input<AbstractControl>();
  minDate = input<Date>(MIN_DATE_M);
  global = input<boolean>(false);
  maxDate = input<Date>(MAX_DATE_M);
  description = input<string>();
  placeholder = input<string[]>([]);
  withoutTime = input<boolean>(false);
  rangeDates = model<RangeDatepickerMInterface>({
    dateStart: null,
    dateEnd: null,
  });
  dateChange = output<RangeDatepickerMInterface>();

  constructor() {
    effect(() => {
      this.switchLanguageService.currentLang();
      this.dateAdapter.setLocale(this.switchLanguageService.currentLang());
    });
  }

  get formControl(): FormControl {
    return this.control() as FormControl;
  }

  onButtonClick() {
    if (this.areBothDatesSelected()) {
      this.rangeDates.set({ dateStart: null, dateEnd: null });
      this.dateChange.emit({ dateStart: null, dateEnd: null });
    } else {
      this.pickerMat().open();
    }
  }

  dateRangeChange(
    dateRangeStart: HTMLInputElement,
    dateRangeEnd: HTMLInputElement,
  ) {
    const rangeDates: RangeDatepickerMInterface = {
      dateStart: dateRangeStart.value
        ? this.toUTCDateString(new Date(dateRangeStart.value))
        : null,
      dateEnd: dateRangeEnd.value
        ? this.toUTCDateString(new Date(dateRangeEnd.value))
        : null,
    };

    if (rangeDates.dateStart && rangeDates.dateEnd) {
      this.dateChange.emit(rangeDates);
    }
  }

  areBothDatesSelected(): boolean {
    return !!(this.rangeDates().dateStart && this.rangeDates().dateEnd);
  }

  toUTCDateString(event: Date): string {
    if (!event) return '';
    const date = new Date(event);
    const year = date.getFullYear();
    const month = date.getMonth();
    const day = date.getDate();

    const utcDate = new Date(Date.UTC(year, month, day, 0, 0, 0));

    if (isNaN(utcDate.getTime())) {
      return date.toISOString();
    }

    if (this.withoutTime()) {
      return utcDate.toISOString().split('T')[0];
    } else {
      return utcDate.toISOString();
    }
  }
}
